import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Check, Trash2, ShieldAlert, Eye } from "lucide-react";

const REASON_LABEL = {
  missing_signature: "Missing signature",
  signature_mismatch: "Signature mismatch",
  validate_failed: "Server validation failed",
  validate_error: "Validation error",
  amount_mismatch: "Amount mismatch",
  unmatched_buyer: "Unmatched buyer",
};

const REASON_STYLE = {
  signature_mismatch: "bg-rose-100 text-rose-800 border-rose-200",
  missing_signature: "bg-rose-100 text-rose-800 border-rose-200",
  amount_mismatch: "bg-amber-100 text-amber-800 border-amber-200",
  validate_failed: "bg-amber-100 text-amber-800 border-amber-200",
  validate_error: "bg-amber-100 text-amber-800 border-amber-200",
  unmatched_buyer: "bg-sky-100 text-sky-800 border-sky-200",
};

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return d; }
}

export default function WebhookFailures() {
  const [statusFilter, setStatusFilter] = useState("open");
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/webhook-failures", { params: { status: statusFilter } });
      setItems(data.items || []);
      setOpenCount(data.open || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load webhook failures");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id, resolved) => {
    try {
      await api.post(`/admin/webhook-failures/${id}/resolve`, { resolved });
      toast.success(resolved ? "Marked resolved" : "Re-opened");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this webhook failure record?")) return;
    try {
      await api.delete(`/admin/webhook-failures/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="border-slate-200" data-testid="admin-webhook-failures">
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-600" />
            <h3 className="font-heading font-semibold text-lg text-[#111827]">Payment webhook failures</h3>
            {openCount > 0 && <Badge className="bg-rose-100 text-rose-800 border-rose-200">{openCount} open</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32" data-testid="wh-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="wh-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Rejected or unmatched Payfast notifications (ITN). Signature mismatches can indicate a wrong passphrase or a spoofed request; "unmatched buyer" means a real payment that needs manual reconciliation (Grant premium by email).
        </p>

        {items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10" data-testid="wh-empty">
            {loading ? "Loading…" : "No webhook failures here. 🎉"}
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {items.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-3 p-3" data-testid={`wh-item-${f.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={REASON_STYLE[f.reason] || "bg-slate-100 text-slate-700 border-slate-200"}>
                      <AlertTriangle className="w-3 h-3 mr-1" />{REASON_LABEL[f.reason] || f.reason}
                    </Badge>
                    {f.resolved && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Resolved</Badge>}
                    <span className="text-xs text-slate-500">{fmt(f.created_at)}</span>
                  </div>
                  <div className="text-sm text-[#111827] mt-1 truncate">{f.detail}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    {f.email && <span>buyer: {f.email}</span>}
                    {f.amount_gross && <span>amount: R{f.amount_gross}</span>}
                    {f.item_name && <span>item: {f.item_name}</span>}
                    {f.pf_payment_id && <span>pf_id: {f.pf_payment_id}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setViewing(f)} data-testid={`wh-view-${f.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Details
                  </Button>
                  {f.resolved ? (
                    <Button size="sm" variant="outline" onClick={() => resolve(f.id, false)} data-testid={`wh-reopen-${f.id}`}>Re-open</Button>
                  ) : (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => resolve(f.id, true)} data-testid={`wh-resolve-${f.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Resolve
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(f.id)} data-testid={`wh-delete-${f.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{REASON_LABEL[viewing?.reason] || viewing?.reason} · {fmt(viewing?.created_at)}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">{viewing?.detail}</p>
          <div className="text-xs text-slate-500">Raw Payfast payload:</div>
          <pre className="text-[11px] bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto" data-testid="wh-raw">
            {JSON.stringify(viewing?.raw || {}, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
